/**
 * Almacenamiento tolerante a un `localStorage` inaccesible.
 *
 * Antes este módulo hacía `const storage = window.localStorage` en scope de módulo. Ese acceso
 * TIRA —no devuelve null— en Safari con ITP, en modo privado y dentro de un iframe de origen
 * opaco, que es justamente cómo se embebe el live en el sitio de un cliente. Al tirar durante
 * la evaluación del módulo se rompía el import y la pantalla quedaba en blanco, sin nada que
 * pudiera atraparlo: no es un fallo del reproductor sino de la carga de la app.
 *
 * Ahora el acceso es perezoso y toda operación cae a un respaldo en memoria. Se pierde la
 * persistencia entre recargas, pero la sesión en curso funciona, que es lo que importa en un
 * evento en vivo.
 */

/** Respaldo cuando el navegador no da almacenamiento. Vive lo que vive la pestaña. */
const memoria = new Map()

const almacenamientoEnMemoria = {
  getItem: (key) => (memoria.has(key) ? memoria.get(key) : null),
  setItem: (key, value) => memoria.set(key, String(value)),
  removeItem: (key) => memoria.delete(key),
}

/**
 * Resuelve el almacenamiento en cada uso. No se memoiza el resultado de un fallo porque los
 * permisos pueden cambiar durante la vida de la pestaña, pero sí se memoiza el éxito.
 */
let almacenamientoOk = null

const resolverAlmacenamiento = () => {
  if (almacenamientoOk) return almacenamientoOk
  try {
    // Leer la propiedad ya puede tirar, así que va dentro del try junto con la prueba de uso:
    // en modo privado de Safari el objeto existe pero `setItem` falla por cuota cero.
    const candidato = window.localStorage
    const sonda = '__gjf_probe__'
    candidato.setItem(sonda, '1')
    candidato.removeItem(sonda)
    almacenamientoOk = candidato
    return candidato
  } catch (_error) {
    return almacenamientoEnMemoria
  }
}

/** Envuelve una operación para que un almacenamiento hostil nunca propague el error. */
const seguro = (operacion, valorPorDefecto) => {
  try {
    return operacion(resolverAlmacenamiento())
  } catch (_error) {
    return valorPorDefecto
  }
}

class StorageService {
  getValue(key) {
    const crudo = seguro((storage) => storage.getItem(key), null)
    if (crudo === null || crudo === undefined) return undefined

    // El contenido puede no ser nuestro: otra app en el mismo origen, una versión anterior o
    // basura. Un JSON inválido acá tiraba y rompía a quien llamara.
    let contenedor
    try {
      contenedor = JSON.parse(crudo)
    } catch (_error) {
      return undefined
    }
    if (!contenedor) return undefined

    if (contenedor.expiry && Date.now() > contenedor.expiry) {
      this.removeValue(key)
      return null
    }

    try {
      return JSON.parse(contenedor.value)
    } catch (_error) {
      return contenedor.value
    }
  }

  setValue(key, value, ttl) {
    const elementToSave = typeof value === 'object' ? JSON.stringify(value) : value
    const elementToSaveWithTTL = {
      value: elementToSave,
      expiry: ttl ? Date.now() + ttl : false, // milisegundos
    }
    // Si la cuota está llena `setItem` tira: se descarta el valor en lugar de romper la app.
    seguro((storage) => storage.setItem(key, JSON.stringify(elementToSaveWithTTL)))
  }

  removeValue(key) {
    seguro((storage) => storage.removeItem(key))
  }
}

const instance = new StorageService()
Object.freeze(instance)

export default instance
